import { useCallback, useState } from "react";
import { useSnackbar } from "notistack";
import Entri, { DnsRecord } from "entrijs";
import { isFreeDomain } from "@/routes/AccountSettings/SenderDetails/utils";
import { fetchApi } from "@tiny/utils";
import {
  DomainValidationOptions,
  DomainValidationReturn,
  EntriTokenResponse,
} from "./Entri.types";

export function useEntri(): DomainValidationReturn {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [isValidating, setIsValidating] = useState(false);
  const applicationId = import.meta.env.VITE_APP_ENTRI_APPLICATION_ID as string;

  const validateDomain = useCallback(
    async (options: DomainValidationOptions) => {
      const { email, onSuccess, onError, spfOverrideSupport = false } = options;

      if (!email) {
        console.error("Email is required for domain validation");
        return;
      }

      if (isFreeDomain(email)) {
        console.log("Skipping validation for free domain:", email);
        onSuccess?.();
        return;
      }

      setIsValidating(true);
      const loadingSnackbarKey = "dns-loading";
      enqueueSnackbar("Loading DNS configuration...", {
        variant: "loading",
        persist: false,
        key: loadingSnackbarKey,
      });

      try {
        const [tokenData, dnsRecords] = await Promise.all([
          fetchApi<EntriTokenResponse>(
            `/notificationservice/v0/entri/token?applicationId=${applicationId}`,
            {
              method: "GET",
            }
          ),
          fetchApi<DnsRecord[]>(
            `/notificationservice/v0/email-auth/dns-records?email=${encodeURIComponent(
              email
            )}`,
            {
              method: "GET",
            }
          ),
        ]);

        const domain = email.split("@")[1];

        if (!tokenData?.auth_token) {
          return;
        }

        if (!dnsRecords) {
          return;
        }

        const config = {
          token: tokenData.auth_token,
          applicationId,
          prefilledDomain: domain,
          dnsRecords,
          spfOverrideSupport,
        };

        const handleEntriClose = (event: Event) => {
          const customEvent = event as CustomEvent<{
            domain: string;
            success: boolean;
            lastStatus: string;
          }>;
          const { success, lastStatus } = customEvent.detail;
          closeSnackbar(loadingSnackbarKey);

          if (success && lastStatus === "FINISHED_SUCCESSFULLY") {
            enqueueSnackbar("DNS configuration completed successfully!", {
              variant: "success",
              autoHideDuration: 5000,
            });
            onSuccess?.();
          } else {
            enqueueSnackbar("DNS configuration was not completed", {
              variant: "warning",
              autoHideDuration: 5000,
            });
          }
          window.removeEventListener("onEntriClose", handleEntriClose);
          setIsValidating(false);
        };
        window.addEventListener("onEntriClose", handleEntriClose, false);
        await Entri.showEntri(config);
      } catch (error) {
        console.error("Error configuring DNS:", error);
        closeSnackbar(loadingSnackbarKey);
        enqueueSnackbar(
          "Failed to configure DNS. Opening help documentation...",
          {
            variant: "error",
            autoHideDuration: 5000,
          }
        );

        window.open(
          "https://support.tinyemail.com/portal/en/kb/articles/how-to-meet-gmail-s-email-sender-requirements",
          "_blank"
        );
        onError?.(error);
        setIsValidating(false);
      }
    },
    [applicationId, enqueueSnackbar, closeSnackbar]
  );

  return {
    validateDomain,
    isValidating,
  };
}
